import React, { FC, useEffect } from 'react';
import { BooleanInput, NumberInput, maxValue, minValue, required, useTranslate } from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import { H3 } from '~/components';
import { useStyles } from '~/hooks/useSolutionsStyles';
import { l, validateInteger } from '~/utils/solutions';
import { Alert } from '@material-ui/lab';

interface TenantPublicApiAuthFormProps {
  keyName: string;
}

export const TenantPublicApiAuthForm: FC<TenantPublicApiAuthFormProps> = ({ keyName }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string, args?: any): string => translate(l(`featuresConfig.${key}`), args);

  const form = useForm();
  const { values } = useFormState();
  const cooldownWindowEnabled = values?.[keyName]?.deviceLinking?.cooldownWindowEnabled;
  const blockAllTransactions = values?.[keyName]?.deviceLinking?.blockAllTransactions;

  useEffect(() => {
    if (!cooldownWindowEnabled) {
      form.change(`${keyName}.deviceLinking.cooldownDurationSeconds`, undefined);
      form.change(`${keyName}.deviceLinking.onboardingPeriodSeconds`, undefined);
      form.change(`${keyName}.deviceLinking.blockAllTransactions`, undefined);
      form.change(`${keyName}.deviceLinking.blockDebitTransactions`, undefined);
    }
  }, [cooldownWindowEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (blockAllTransactions) {
      form.change(`${keyName}.deviceLinking.blockDebitTransactions`, undefined);
    }
  }, [blockAllTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <H3 topSpacing={0} bottomSpacing={2}>
        {t('otpSmsTitle')}
      </H3>
      <NumberInput
        label={t('otpSmsLength')}
        source={`${keyName}.otp.sms.length`}
        min={OTP_SMS_LENGTH_MIN}
        max={OTP_SMS_LENGTH_MAX}
        step={1}
        validate={[
          required(),
          minValue(OTP_SMS_LENGTH_MIN),
          maxValue(OTP_SMS_LENGTH_MAX),
          validateInteger,
        ]}
        className={classes.inputWidth}
      />
      <BooleanInput
        label={t('otpSmsDisableManualEntry')}
        source={`${keyName}.otp.sms.disableManualEntry`}
        className={classes.inputWidth}
      />
      <Alert className={classes.marginBottom} severity='info'>
        {t('stepUpOtpSmsExpirationTimeoutInfo')}
      </Alert>
      <NumberInput
        label={t('stepUpOtpSmsExpirationTimeout')}
        source={`${keyName}.otp.sms.smsOtpExpirationSeconds`}
        min={OTP_EXPIRATION_MIN_MINUTES}
        max={OTP_EXPIRATION_MAX_MINUTES}
        step={1}
        validate={[
          minValue(
            OTP_EXPIRATION_MIN_SECONDS,
            t('otpSmsExpirationTimeoutMinError', { min: OTP_EXPIRATION_MIN_MINUTES })
          ),
          maxValue(
            OTP_EXPIRATION_MAX_SECONDS,
            t('otpSmsExpirationTimeoutMaxError', { max: OTP_EXPIRATION_MAX_MINUTES })
          ),
          required(),
        ]}
        format={(value?: number) => (value ? Math.floor(value / 60) : '')}
        parse={(value?: number) => (value ? value * 60 : undefined)}
        className={classes.inputWidth}
      />
      <H3 topSpacing={2} bottomSpacing={2}>
        {t('authSessionTitle')}
      </H3>
      <NumberInput
        label={t('authSessionLifetime')}
        source={`${keyName}.authSession.authenticationSessionLifetimeSeconds`}
        min={SESSION_LIFETIME_MIN_MINUTES}
        max={SESSION_LIFETIME_MAX_MINUTES}
        step={1}
        validate={[
          minValue(
            SESSION_LIFETIME_MIN_SECONDS,
            t('authSessionLifetimeMinError', { min: SESSION_LIFETIME_MIN_MINUTES })
          ),
          maxValue(
            SESSION_LIFETIME_MAX_SECONDS,
            t('authSessionLifetimeMaxError', { max: SESSION_LIFETIME_MAX_MINUTES })
          ),
          required(),
        ]}
        format={(value?: number) => (value ? Math.floor(value / 60) : '')}
        parse={(value?: number) => (value ? value * 60 : undefined)}
        className={classes.inputWidth}
      />
      <H3 topSpacing={2} bottomSpacing={2}>
        {t('deviceLinkingTitle')}
      </H3>
      <NumberInput
        label={t('maxDevices')}
        source={`${keyName}.deviceLinking.maxDevices`}
        min={1}
        step={1}
        validate={[minValue(1), validateInteger]}
        format={(value?: number) => (value ? Math.floor(value) : '')}
        parse={(value?: number) => (value && Number(value) > 0 ? Number(value) : undefined)}
        className={classes.inputWidth}
      />
      <BooleanInput
        label={t('cooldownWindowEnabled')}
        source={`${keyName}.deviceLinking.cooldownWindowEnabled`}
        className={classes.inputWidth}
      />
      {cooldownWindowEnabled && (
        <>
          <NumberInput
            label={t('cooldownDuration')}
            source={`${keyName}.deviceLinking.cooldownDurationSeconds`}
            min={0}
            step={1}
            format={(value?: number) => (value ? Math.floor(value / 3600) : '')}
            parse={(value?: number) => (value ? value * 3600 : 0)}
            className={classes.inputWidth}
          />
          <NumberInput
            label={t('onboardingPeriod')}
            source={`${keyName}.deviceLinking.onboardingPeriodSeconds`}
            min={0}
            step={1}
            format={(value?: number) => (value ? Math.floor(value / 60) : '')}
            parse={(value?: number) => (value ? value * 60 : 0)}
            className={classes.inputWidth}
          />
          <BooleanInput
            label={t('blockAllTransactions')}
            source={`${keyName}.deviceLinking.blockAllTransactions`}
            className={classes.inputWidth}
          />
          {!blockAllTransactions && (
            <BooleanInput
              label={t('blockDebitTransactions')}
              source={`${keyName}.deviceLinking.blockDebitTransactions`}
              className={classes.inputWidth}
            />
          )}
        </>
      )}
    </>
  );
};

// OTP SMS length constants
const OTP_SMS_LENGTH_MIN = 4;
const OTP_SMS_LENGTH_MAX = 10;

// OTP expiration constants
const OTP_EXPIRATION_MIN_MINUTES = 1;
const OTP_EXPIRATION_MAX_MINUTES = 5;
const OTP_EXPIRATION_MIN_SECONDS = OTP_EXPIRATION_MIN_MINUTES * 60;
const OTP_EXPIRATION_MAX_SECONDS = OTP_EXPIRATION_MAX_MINUTES * 60;

// Auth session lifetime constants
const SESSION_LIFETIME_MIN_MINUTES = 1;
const SESSION_LIFETIME_MAX_MINUTES = 30;
const SESSION_LIFETIME_MIN_SECONDS = SESSION_LIFETIME_MIN_MINUTES * 60;
const SESSION_LIFETIME_MAX_SECONDS = SESSION_LIFETIME_MAX_MINUTES * 60;
